import React, { useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Centroide automático de los puntos visibles
function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
}

// Icono coloreado por cadena (divIcon, no requiere assets)
function espacioIcon(color = "#f59e0b", size = 22) {
  const c = color || "#f59e0b";
  return L.divIcon({
    className: "espacio-marker",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${c};
      border:2px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,0.5);
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Centro por defecto (España) cuando NO hay puntos
const DEFAULT_CENTER = [40.0, -3.7];
const DEFAULT_ZOOM = 5;

// Icono de cluster customizado (amber/zinc, encaja con el tema)
function clusterIcon(cluster) {
  const count = cluster.getChildCount();
  let size = 36;
  let bg = "#f59e0b"; // amber
  if (count >= 100) { size = 52; bg = "#dc2626"; }
  else if (count >= 25) { size = 44; bg = "#ea580c"; }
  return L.divIcon({
    className: "espacios-cluster",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${bg};color:#0a0a0c;
      display:flex;align-items:center;justify-content:center;
      font-weight:800;font-size:13px;font-family:sans-serif;
      border:3px solid rgba(255,255,255,0.85);
      box-shadow:0 2px 8px rgba(0,0,0,0.5);
    ">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Mapa reutilizable de espacios con marcadores coloreados por cadena.
 *
 * @param {Array} espacios - lista de espacios ({id, nombre, direccion, cadena_id, latitud, longitud})
 * @param {Array} cadenas  - lista de cadenas ({id, nombre, color})
 * @param {number} height  - altura del mapa en px (default 500)
 * @param {boolean} showLegend - mostrar leyenda de cadenas (default true)
 * @param {boolean} cluster - activar clustering de marcadores (default true)
 */
export default function EspaciosMap({ espacios = [], cadenas = [], height = 500, showLegend = true, cluster = true }) {
  const cadenaMap = useMemo(() => {
    const m = {};
    cadenas.forEach((c) => { m[c.id] = c; });
    return m;
  }, [cadenas]);

  // Solo espacios con coordenadas válidas
  const geo = useMemo(
    () => espacios.filter((e) => typeof e.latitud === "number" && typeof e.longitud === "number"),
    [espacios]
  );
  const sinGeo = espacios.length - geo.length;
  const points = useMemo(() => geo.map((e) => [e.latitud, e.longitud]), [geo]);

  // Leyenda: solo cadenas presentes entre los espacios visibles
  const cadenasPresentes = useMemo(() => {
    const ids = new Set(geo.map((e) => e.cadena_id).filter(Boolean));
    return cadenas.filter((c) => ids.has(c.id));
  }, [geo, cadenas]);

  return (
    <div
      className="rounded-lg overflow-hidden border border-zinc-800 bg-zinc-950 relative"
      style={{ height }}
      data-testid="espacios-map"
    >
      <MapContainer
        center={points[0] || DEFAULT_CENTER}
        zoom={points.length ? 6 : DEFAULT_ZOOM}
        style={{ width: "100%", height: "100%", background: "#0a0a0c" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <AutoFit points={points} />

        {(() => {
          const markers = geo.map((e) => {
            const cad = cadenaMap[e.cadena_id];
            const color = cad?.color || "#f59e0b";
            return (
              <Marker
                key={e.id}
                position={[e.latitud, e.longitud]}
                icon={espacioIcon(color, 22)}
              >
                <Popup>
                  <div style={{ fontSize: 12, minWidth: 180 }}>
                    <div style={{ fontWeight: 700, color: "#111", marginBottom: 2 }}>
                      {e.nombre}
                    </div>
                    {e.direccion && (
                      <div style={{ color: "#555" }}>
                        {e.direccion}
                        {e.ciudad ? `, ${e.ciudad}` : ""}
                        {e.provincia ? ` (${e.provincia})` : ""}
                      </div>
                    )}
                    {cad && (
                      <div style={{
                        marginTop: 4, fontSize: 10,
                        display: "inline-block",
                        padding: "1px 6px", borderRadius: 4,
                        background: color, color: "#0a0a0c",
                        fontWeight: 600,
                      }}>{cad.nombre}</div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          });
          return cluster ? (
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={clusterIcon}
              showCoverageOnHover={false}
              spiderfyOnMaxZoom
              maxClusterRadius={50}
              disableClusteringAtZoom={14}
            >
              {markers}
            </MarkerClusterGroup>
          ) : markers;
        })()}
      </MapContainer>

      {/* Leyenda (esquina superior derecha) */}
      {showLegend && cadenasPresentes.length > 0 && (
        <div className="absolute top-2 right-2 z-[400] bg-zinc-950/95 border border-zinc-800 rounded-md p-2 max-w-[180px] max-h-[40%] overflow-y-auto shadow-lg" data-testid="espacios-map-legend">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-semibold">
            Cadenas
          </div>
          <div className="space-y-1">
            {cadenasPresentes.map((c) => (
              <div key={c.id} className="flex items-center gap-1.5 text-[11px] text-zinc-300">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full border border-white/40 flex-shrink-0"
                  style={{ backgroundColor: c.color || "#f59e0b" }}
                />
                <span className="truncate">{c.nombre}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sinGeo > 0 && (
        <div className="absolute bottom-2 left-2 z-[400] bg-amber-500/15 border border-amber-500/40 rounded px-2.5 py-1 text-[11px] text-amber-300 shadow">
          ⚠️ {sinGeo} sin geolocalizar
        </div>
      )}

      {geo.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-zinc-950/85 border border-zinc-800 rounded-lg px-4 py-3 text-center text-zinc-400 text-sm">
            No hay espacios geolocalizados para mostrar.
          </div>
        </div>
      )}
    </div>
  );
}
